import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button, ButtonGroup, Dropdown, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { genericGetOne, genericGetAll, getAllOrgSettings, getImagesForEntry, getOrgInfo } from "../generic/crud";
import { ENTITY_ID_RAW_PURCHASE, ENTITY_ID_TAX_PLAN } from "../generic/Constants";
import { API_URL } from "../../config/api.config";
import { useAuth } from "../../modules/auth";
import { showToast, TOAST_FAILURE } from "../../utils/toast/Toast";
import { formatDateTime } from "../../utils/Utils";
import { DisplayTogglePill } from "../../generic-components/DisplayTogglePill";
import { PurchaseThermalReceipt } from "./PurchaseThermalReceipt";

// Remembers the operator's preferred ticket layout across sessions
const PURCHASE_PRINT_MODE_KEY = 'purchase_print_mode';

export function PurchasePrintSummary({ id }) {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [purchaseData, setPurchaseData] = useState(null);
    const [taxPlansLookup, setTaxPlansLookup] = useState({});
    const [showImagesInPrint, setShowImagesInPrint] = useState(false);
    const [hideOrgName, setHideOrgName] = useState(false);
    const [hideAmount, setHideAmount] = useState(false);
    const [capturedImages, setCapturedImages] = useState([]);
    const [orgData, setOrgData] = useState(null);
    // 'BIG' = full A4 ticket, 'THERMAL' = 80mm roll printer receipt
    const [printMode, setPrintMode] = useState(() =>
        localStorage.getItem(PURCHASE_PRINT_MODE_KEY) === 'THERMAL' ? 'THERMAL' : 'BIG'
    );
    const pendingPrintRef = useRef(false);

    // The ticket is handed to the supplier's driver, so the letterhead needs the address and phone the login
    // token does not carry. org-info serves the outpost's own stored copy, so
    // this still works with no server in reach.
    useEffect(() => {
        getOrgInfo()
            .then(({ data }) => setOrgData(data || null))
            .catch(() => setOrgData(null));
    }, []);

    // Persist the selected layout so it becomes the default next time
    useEffect(() => {
        localStorage.setItem(PURCHASE_PRINT_MODE_KEY, printMode);
    }, [printMode]);

    // Load org settings to check if images should be shown in print
    useEffect(() => {
        getAllOrgSettings()
            .then((response) => {
                const settings = response.data || [];
                const setting = settings.find(s => s.typeName === 'SHOW_IMAGES_IN_PRINT');
                if (setting) {
                    setShowImagesInPrint(setting.value === true || setting.value === 'true');
                }
                const hideOrgNameSetting = settings.find(s => s.typeName === 'HIDE_ORG_NAME_IN_PURCHASE_TICKET');
                if (hideOrgNameSetting) {
                    setHideOrgName(hideOrgNameSetting.value === true || hideOrgNameSetting.value === 'true');
                }
                const hideAmountSetting = settings.find(s => s.typeName === 'HIDE_AMOUNT_IN_PURCHASE_TICKET');
                if (hideAmountSetting) {
                    setHideAmount(hideAmountSetting.value === true || hideAmountSetting.value === 'true');
                }
            })
            .catch((error) => {
                console.error("Error loading org settings:", error);
            });
    }, []);

    // Load captured images for this purchase
    useEffect(() => {
        if (id && showImagesInPrint) {
            getImagesForEntry('PURCHASE', id)
                .then((response) => {
                    setCapturedImages(response.data?.images || []);
                })
                .catch((error) => {
                    console.error("Error loading images:", error);
                });
        }
    }, [id, showImagesInPrint]);

    // Load tax plans for calculations
    useEffect(() => {
        genericGetAll(ENTITY_ID_TAX_PLAN, { pageNumber: 1, pageSize: 1000 })
            .then((response) => {
                const lookup = {};
                const responseData = response?.data || response;
                const taxPlans = responseData?.entities || responseData?.data || [];

                if (Array.isArray(taxPlans)) {
                    taxPlans.forEach(taxPlan => {
                        if (taxPlan.id) lookup[taxPlan.id] = taxPlan;
                        if (taxPlan.name) lookup[taxPlan.name] = taxPlan;
                    });
                }
                setTaxPlansLookup(lookup);
            })
            .catch((error) => {
                console.error("Error loading tax plans:", error);
            });
    }, []);

    // Load purchase data
    useEffect(() => {
        if (id) {
            setIsLoading(true);
            genericGetOne(ENTITY_ID_RAW_PURCHASE, id)
                .then(({ data }) => {
                    setPurchaseData(data);
                    setIsLoading(false);
                })
                .catch((error) => {
                    showToast("Error loading purchase: " + error.message, TOAST_FAILURE);
                    setIsLoading(false);
                    navigate('/purchases-list');
                });
        }
    }, [id, navigate]);

    // Parse number helper
    const parseNumber = (value) => {
        if (value === null || value === undefined || value === '') return 0;
        const parsed = parseFloat(value);
        return isNaN(parsed) ? 0 : parsed;
    };

    // Format currency
    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined || isNaN(amount)) return '₹0.00';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount);
    };

    // Format date
    const formatDate = (dateStr) => formatDateTime(dateStr);

    // Get tax percentage from lookup
    const getTaxPercentage = useCallback((tax) => {
        let percentage = tax.percentage || tax.taxPercentage;
        if ((percentage === null || percentage === undefined) && tax.id && taxPlansLookup[tax.id]) {
            percentage = taxPlansLookup[tax.id].taxPercentage;
        }
        if ((percentage === null || percentage === undefined) && tax.name && taxPlansLookup[tax.name]) {
            percentage = taxPlansLookup[tax.name].taxPercentage;
        }
        return parseNumber(percentage);
    }, [taxPlansLookup]);

    // Calculate bill totals
    const calculateTotals = useCallback(() => {
        if (!purchaseData) return { subtotal: 0, totalDiscount: 0, totalTax: 0, grandTotal: 0 };

        const items = purchaseData.itemsList || [];
        let subtotal = 0;
        let totalDiscount = 0;
        let totalTax = 0;

        items.forEach(item => {
            const price = parseNumber(item.price);
            const quantity = parseNumber(item.quantity);
            const itemDiscount = parseNumber(item.discountAmount);
            const lineSubtotal = price * quantity;
            const afterDiscount = lineSubtotal - itemDiscount;

            subtotal += lineSubtotal;
            totalDiscount += itemDiscount;

            if (item.taxPlans && item.taxPlans.length > 0) {
                item.taxPlans.forEach(tax => {
                    const taxPercent = getTaxPercentage(tax);
                    totalTax += (afterDiscount * taxPercent) / 100;
                });
            }
        });

        const roundOff = parseNumber(purchaseData.roundOffAmount);
        const grandTotal = (subtotal - totalDiscount) + totalTax + roundOff;

        return { subtotal, totalDiscount, totalTax, roundOff, grandTotal };
    }, [purchaseData, getTaxPercentage]);

    // Handle print — switch preview to the requested mode first so the
    // print dialog picks up the matching layout and page size
    const handlePrint = (mode) => {
        if (mode !== printMode) {
            pendingPrintRef.current = true;
            setPrintMode(mode);
        } else {
            window.print();
        }
    };

    // Fire the pending print once the preview has re-rendered in the new mode
    useEffect(() => {
        if (pendingPrintRef.current) {
            pendingPrintRef.current = false;
            const timer = setTimeout(() => window.print(), 150);
            return () => clearTimeout(timer);
        }
    }, [printMode]);

    // Handle back
    const handleBack = () => {
        navigate('/purchases-list');
    };

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                <Spinner animation="border" variant="primary" />
                <span className="ms-2">Loading purchase data...</span>
            </div>
        );
    }

    if (!purchaseData) {
        return (
            <div className="text-center py-5">
                <p className="text-muted">Purchase not found</p>
                <Button variant="primary" onClick={handleBack}>Back to Purchases List</Button>
            </div>
        );
    }

    const totals = calculateTotals();
    const correctionAmount = parseNumber(purchaseData.loadedWeightCorrection);
    const netWeight = parseNumber(purchaseData.loadedWeight) - correctionAmount - parseNumber(purchaseData.emptyWeight);

    // Organization details from current user
    // Organization details - the stored record where it could be read,
    // otherwise whatever the login token carries.
    const orgName = orgData?.name || currentUser?.orgName || currentUser?.companyName || 'Organization Name';
    const orgAddress = [orgData?.address, orgData?.city, orgData?.pinCode]
        .filter((part) => part && String(part).trim() && String(part).trim() !== '0')
        .join(', ');
    const orgContact = orgData?.phone ? `Phone: ${orgData.phone}` : '';

    return (
        <>
            {/* Print Controls - Hidden during print */}
            <div className="d-print-none mb-4 d-flex justify-content-between align-items-center">
                <Button variant="outline-secondary" onClick={handleBack}>
                    <i className="bi bi-arrow-left me-2"></i>
                    Back to Purchases List
                </Button>
                <div className="d-flex gap-2">
                    <Button variant="success" onClick={() => navigate('/purchase-new')}>
                        <i className="bi bi-plus-lg me-2"></i>
                        New Purchase
                    </Button>
                    <Dropdown as={ButtonGroup}>
                        <Button variant="primary" onClick={() => handlePrint(printMode)}>
                            <i className="bi bi-printer me-2"></i>
                            {printMode === 'THERMAL' ? 'Print Thermal' : 'Print Ticket'}
                        </Button>
                        <Dropdown.Toggle split variant="primary" />
                        <Dropdown.Menu align="end">
                            <Dropdown.Item active={printMode === 'BIG'} onClick={() => setPrintMode('BIG')}>
                                <i className="bi bi-file-earmark me-2"></i>
                                Full Page (A4)
                            </Dropdown.Item>
                            <Dropdown.Item active={printMode === 'THERMAL'} onClick={() => setPrintMode('THERMAL')}>
                                <i className="bi bi-receipt me-2"></i>
                                Thermal (80mm)
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </div>

            {/* Display toggles — pre-set from org settings, user can override before printing.
                Constrained to the same 800px centered column as the print page below
                so the strip's left edge aligns with the print ticket's left edge. */}
            <div className="d-print-none mb-3" style={{ maxWidth: '800px', margin: '0 auto', padding: '0 20px' }}>
                <div className="d-flex flex-wrap gap-2 align-items-center bg-light px-3 py-2 rounded">
                    <small className="text-muted fw-semibold me-2">Display:</small>
                    <DisplayTogglePill
                        icon="bi-currency-rupee"
                        label="Price"
                        active={!hideAmount}
                        onClick={() => setHideAmount(h => !h)}
                    />
                    {printMode !== 'THERMAL' && (
                        <DisplayTogglePill
                            icon="bi-image"
                            label="Photo"
                            active={showImagesInPrint}
                            onClick={() => setShowImagesInPrint(s => !s)}
                        />
                    )}
                    <DisplayTogglePill
                        icon="bi-receipt"
                        label="Thermal"
                        active={printMode === 'THERMAL'}
                        onClick={() => setPrintMode(m => m === 'THERMAL' ? 'BIG' : 'THERMAL')}
                    />
                    <DisplayTogglePill
                        icon="bi-building"
                        label="Org Name"
                        active={!hideOrgName}
                        onClick={() => setHideOrgName(h => !h)}
                    />
                </div>
            </div>

            {/* Printable Content */}
            {printMode === 'THERMAL' ? (
                <PurchaseThermalReceipt
                    purchaseData={purchaseData}
                    orgName={orgName}
                    orgAddress={orgAddress}
                    orgContact={orgContact}
                    hideOrgName={hideOrgName}
                    hideAmount={hideAmount}
                    totals={totals}
                    netWeight={netWeight}
                    grossWeight={parseNumber(purchaseData.loadedWeight) - correctionAmount}
                    formatCurrency={formatCurrency}
                    formatDate={formatDate}
                />
            ) : (
            <div className="print-receipt receipt-paper-a4 bg-white" style={printStyles.container}>
                {/* Header - Organization Details */}
                <div style={printStyles.header}>
                    {!hideOrgName && (
                        <>
                            <h2 style={printStyles.orgName}>{orgName}</h2>
                            {orgAddress && <p style={printStyles.orgDetail}>{orgAddress}</p>}
                            {orgContact && <p style={printStyles.orgDetail}>{orgContact}</p>}
                        </>
                    )}
                    <p style={printStyles.orgSubtitle}>Purchase Ticket</p>
                </div>

                {/* GRN Info Row */}
                <div style={printStyles.billInfoRow}>
                    <div style={printStyles.billInfoLeft}>
                        <table style={printStyles.infoTable}>
                            <tbody>
                                <tr>
                                    <td style={printStyles.infoLabel}>Ticket No:</td>
                                    <td style={printStyles.infoValue}><strong>{purchaseData.purchaseTicketNumber || '-'}</strong></td>
                                </tr>
                                <tr>
                                    <td style={printStyles.infoLabel}>Date:</td>
                                    <td style={printStyles.infoValue}>{formatDate(purchaseData.purchaseDate)}</td>
                                </tr>
                                <tr>
                                    <td style={printStyles.infoLabel}>Vehicle:</td>
                                    <td style={printStyles.infoValue}><strong>{purchaseData.vehicleNumber || '-'}</strong></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div style={printStyles.billInfoRight}>
                        <table style={printStyles.infoTable}>
                            <tbody>
                                <tr>
                                    <td style={printStyles.infoLabel}>Supplier:</td>
                                    <td style={printStyles.infoValue}><strong>{purchaseData.supplier?.name || '-'}</strong></td>
                                </tr>
                                <tr>
                                    <td style={printStyles.infoLabel}>Driver:</td>
                                    <td style={printStyles.infoValue}>{purchaseData.driverName || '-'}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Weight Information */}
                <div style={printStyles.weightSection}>
                    <table style={printStyles.weightTable}>
                        <tbody>
                            <tr>
                                <td style={printStyles.weightCell}>
                                    <span style={printStyles.weightLabel}>Empty Weight</span>
                                    <span style={printStyles.weightValue}>{purchaseData.emptyWeight ? `${purchaseData.emptyWeight} Ton` : '-'}</span>
                                </td>
                                <td style={printStyles.weightCell}>
                                    <span style={printStyles.weightLabel}>Loaded Weight</span>
                                    <span style={printStyles.weightValue}>{purchaseData.loadedWeight ? `${(parseNumber(purchaseData.loadedWeight) - correctionAmount).toFixed(3).replace(/\.?0+$/, '')} Ton` : '-'}</span>
                                </td>
                                <td style={{ ...printStyles.weightCell, ...printStyles.netWeightCell }}>
                                    <span style={printStyles.weightLabel}>Net Weight</span>
                                    <span style={printStyles.netWeightValue}>{netWeight > 0 ? `${netWeight.toFixed(3)} Ton` : '-'}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Captured Images */}
                {showImagesInPrint && capturedImages.length > 0 && (
                    <div style={printStyles.imagesSection}>
                        <div style={printStyles.imagesSectionHeader}>Captured Images</div>
                        <div style={printStyles.imagesGrid}>
                            {capturedImages.map((image) => (
                                <div key={image.primaryId} style={printStyles.imageBox}>
                                    <img
                                        src={`${API_URL}/api/vehicle-capture/image/${image.primaryId}`}
                                        alt={image.imageType === 'EMPTY_WEIGHT' ? 'Empty Weight' : 'Loaded Weight'}
                                        style={printStyles.imageImg}
                                    />
                                    <div style={printStyles.imageLabel}>
                                        {image.imageType === 'EMPTY_WEIGHT' ? 'Empty Weight' : 'Loaded Weight'}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Items Table */}
                <div style={printStyles.itemsSection}>
                    <table style={printStyles.itemsTable}>
                        <thead>
                            <tr style={printStyles.itemsHeaderRow}>
                                <th style={{ ...printStyles.itemsTh, width: '5%' }}>#</th>
                                <th style={{ ...printStyles.itemsTh, width: hideAmount ? '85%' : '35%', textAlign: 'left' }}>Item Description</th>
                                {!hideAmount && <th style={{ ...printStyles.itemsTh, width: '12%' }}>Rate</th>}
                                <th style={{ ...printStyles.itemsTh, width: '10%' }}>Qty</th>
                                {!hideAmount && <th style={{ ...printStyles.itemsTh, width: '10%' }}>Disc</th>}
                                {!hideAmount && <th style={{ ...printStyles.itemsTh, width: '13%' }}>Tax</th>}
                                {!hideAmount && <th style={{ ...printStyles.itemsTh, width: '15%' }}>Amount</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {(purchaseData.itemsList || []).map((item, index) => {
                                const price = parseNumber(item.price);
                                const quantity = parseNumber(item.quantity);
                                const discount = parseNumber(item.discountAmount);
                                const lineSubtotal = price * quantity;
                                const afterDiscount = lineSubtotal - discount;

                                let taxAmount = 0;
                                let taxInfo = [];
                                if (item.taxPlans && item.taxPlans.length > 0) {
                                    item.taxPlans.forEach(tax => {
                                        const taxPercent = getTaxPercentage(tax);
                                        const taxAmt = (afterDiscount * taxPercent) / 100;
                                        taxAmount += taxAmt;
                                        taxInfo.push(`${taxPercent}%`);
                                    });
                                }

                                const lineTotal = afterDiscount + taxAmount;

                                return (
                                    <tr key={index} style={printStyles.itemsRow}>
                                        <td style={printStyles.itemsTd}>{index + 1}</td>
                                        <td style={{ ...printStyles.itemsTd, textAlign: 'left' }}>
                                            {item.inventoryItemName || 'Item'}
                                            {item.itemType === 'SERVICE' && <span style={printStyles.serviceTag}> (Service)</span>}
                                        </td>
                                        {!hideAmount && <td style={printStyles.itemsTd}>{formatCurrency(price)}</td>}
                                        <td style={printStyles.itemsTd}>{quantity} {item.unitSymbol || ''}</td>
                                        {!hideAmount && <td style={printStyles.itemsTd}>{discount > 0 ? formatCurrency(discount) : '-'}</td>}
                                        {!hideAmount && (
                                            <td style={printStyles.itemsTd}>
                                                {taxAmount > 0 ? (
                                                    <span>{formatCurrency(taxAmount)} <small>({taxInfo.join(', ')})</small></span>
                                                ) : '-'}
                                            </td>
                                        )}
                                        {!hideAmount && <td style={{ ...printStyles.itemsTd, fontWeight: 'bold' }}>{formatCurrency(lineTotal)}</td>}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Totals Section */}
                <div style={printStyles.totalsSection}>
                    <div style={printStyles.totalsLeft}>
                        {/* Additional Info */}
                        <table style={printStyles.additionalInfoTable}>
                            <tbody>
                                {purchaseData.inspectionNotes && (
                                    <tr>
                                        <td style={printStyles.addInfoLabel}>Inspection:</td>
                                        <td style={printStyles.addInfoValue}>{purchaseData.inspectionNotes}</td>
                                    </tr>
                                )}
                                {purchaseData.remarks && (
                                    <tr>
                                        <td style={printStyles.addInfoLabel}>Remarks:</td>
                                        <td style={printStyles.addInfoValue}>{purchaseData.remarks}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {!hideAmount && (
                        <div style={printStyles.totalsRight}>
                            <table style={printStyles.totalsTable}>
                                <tbody>
                                    <tr>
                                        <td style={printStyles.totalsLabel}>Subtotal:</td>
                                        <td style={printStyles.totalsValue}>{formatCurrency(totals.subtotal)}</td>
                                    </tr>
                                    {totals.totalDiscount > 0 && (
                                        <tr>
                                            <td style={printStyles.totalsLabel}>Discount:</td>
                                            <td style={{ ...printStyles.totalsValue, color: '#dc3545' }}>-{formatCurrency(totals.totalDiscount)}</td>
                                        </tr>
                                    )}
                                    {totals.totalTax > 0 && (
                                        <tr>
                                            <td style={printStyles.totalsLabel}>Tax:</td>
                                            <td style={printStyles.totalsValue}>{formatCurrency(totals.totalTax)}</td>
                                        </tr>
                                    )}
                                    {totals.roundOff !== 0 && (
                                        <tr>
                                            <td style={printStyles.totalsLabel}>Round Off:</td>
                                            <td style={printStyles.totalsValue}>{totals.roundOff >= 0 ? '+' : ''}{formatCurrency(totals.roundOff)}</td>
                                        </tr>
                                    )}
                                    <tr style={printStyles.grandTotalRow}>
                                        <td style={printStyles.grandTotalLabel}>Grand Total:</td>
                                        <td style={printStyles.grandTotalValue}>{formatCurrency(totals.grandTotal)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Signature Section */}
                <div style={printStyles.signatureSection}>
                    <div style={printStyles.signatureBox}>
                        <div style={printStyles.signatureLine}></div>
                        <div style={printStyles.signatureLabel}>Received By</div>
                    </div>
                    <div style={printStyles.signatureBox}>
                        <div style={printStyles.signatureLine}></div>
                        <div style={printStyles.signatureLabel}>Supplier Signature</div>
                    </div>
                    <div style={printStyles.signatureBox}>
                        <div style={printStyles.signatureLine}></div>
                        <div style={printStyles.signatureLabel}>Authorized Signature</div>
                    </div>
                </div>

                {/* Footer */}
                <div style={printStyles.footer}>
                    <p style={printStyles.footerText}>Thank you for your business!</p>
                    <p style={printStyles.footerNote}>This is a computer generated purchase ticket.</p>
                    <p style={printStyles.footerBranding}>Powered by Revcon Technologies • revcontech.com</p>
                </div>
            </div>
            )}

            {/* Print-specific styles */}
            <style>{`
                /* On screen, show the ticket as a real A4 sheet of paper */
                @media screen {
                    .receipt-paper-a4 {
                        aspect-ratio: 210 / 297;
                        padding: 15mm 14mm !important;
                        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06), 0 2px 6px rgba(0, 0, 0, 0.08), 0 18px 40px rgba(0, 0, 0, 0.16);
                        margin-bottom: 40px !important;
                    }
                }

                @media print {
                    @page {
                        size: ${printMode === 'THERMAL' ? '80mm auto' : 'A4'};
                        margin: ${printMode === 'THERMAL' ? '0' : '10mm'};
                    }

                    body {
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }

                    .d-print-none {
                        display: none !important;
                    }

                    .print-receipt {
                        width: 100% !important;
                        max-width: none !important;
                        margin: 0 !important;
                        padding: ${printMode === 'THERMAL' ? '4mm 3mm' : '0'} !important;
                        box-shadow: none !important;
                    }

                    /* Hide Metronic layout elements */
                    .aside, .sidebar, .header, .footer, .toolbar, .scrolltop,
                    .app-aside, .app-sidebar, .app-header, .app-footer, .app-toolbar {
                        display: none !important;
                    }

                    .wrapper, .app-wrapper, .content, .app-content {
                        padding: 0 !important;
                        margin: 0 !important;
                    }
                }
            `}</style>
        </>
    );
}

// Print-optimized styles (same structure as SalePrintSummary)
const printStyles = {
    container: {
        maxWidth: '800px',
        margin: '0 auto',
        padding: '20px',
        fontFamily: 'Arial, sans-serif',
        fontSize: '12px',
        lineHeight: '1.4',
        color: '#333',
    },
    header: {
        textAlign: 'center',
        borderBottom: '2px solid #1b365d',
        paddingBottom: '10px',
        marginBottom: '15px',
    },
    orgName: {
        margin: '0 0 3px 0',
        fontSize: '24px',
        fontWeight: 800,
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        color: '#1b365d',
    },
    orgDetail: {
        margin: '0 0 1px 0',
        fontSize: '11px',
        color: '#5a6474',
        lineHeight: 1.4,
    },
    orgSubtitle: {
        margin: '7px 0 0 0',
        display: 'inline-block',
        padding: '2px 12px',
        border: '1px solid #d3e0f0',
        borderRadius: '3px',
        backgroundColor: '#eef3fa',
        fontSize: '12px',
        fontWeight: 600,
        letterSpacing: '1px',
        textTransform: 'uppercase',
        color: '#1b365d',
    },
    billInfoRow: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '15px',
        padding: '10px',
        backgroundColor: '#f8f9fa',
        border: '1px solid #dee2e6',
    },
    billInfoLeft: {
        flex: 1,
    },
    billInfoRight: {
        flex: 1,
        textAlign: 'right',
    },
    infoTable: {
        width: '100%',
        borderCollapse: 'collapse',
    },
    infoLabel: {
        padding: '2px 10px 2px 0',
        color: '#666',
        fontSize: '11px',
        width: '80px',
    },
    infoValue: {
        padding: '2px 0',
        fontSize: '12px',
    },
    weightSection: {
        marginBottom: '15px',
    },
    weightTable: {
        width: '100%',
        borderCollapse: 'collapse',
        border: '1px solid #dee2e6',
    },
    weightCell: {
        padding: '8px',
        textAlign: 'center',
        borderRight: '1px solid #dee2e6',
        width: '33.33%',
    },
    netWeightCell: {
        backgroundColor: '#d1e7dd',
        borderRight: 'none',
    },
    weightLabel: {
        display: 'block',
        fontSize: '10px',
        color: '#666',
        marginBottom: '3px',
    },
    weightValue: {
        display: 'block',
        fontSize: '14px',
        fontWeight: 'bold',
    },
    netWeightValue: {
        display: 'block',
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#198754',
    },
    itemsSection: {
        marginBottom: '15px',
    },
    itemsTable: {
        width: '100%',
        borderCollapse: 'collapse',
        border: '1px solid #333',
    },
    itemsHeaderRow: {
        backgroundColor: '#333',
        color: '#fff',
    },
    itemsTh: {
        padding: '8px 5px',
        textAlign: 'center',
        fontSize: '11px',
        fontWeight: 'bold',
        borderRight: '1px solid #555',
    },
    itemsRow: {
        borderBottom: '1px solid #dee2e6',
    },
    itemsTd: {
        padding: '6px 5px',
        textAlign: 'center',
        fontSize: '11px',
        borderRight: '1px solid #dee2e6',
    },
    serviceTag: {
        fontSize: '9px',
        color: '#6c757d',
        fontStyle: 'italic',
    },
    totalsSection: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '15px',
    },
    totalsLeft: {
        flex: 1,
        paddingRight: '20px',
    },
    totalsRight: {
        width: '250px',
    },
    additionalInfoTable: {
        width: '100%',
        fontSize: '11px',
    },
    addInfoLabel: {
        padding: '3px 10px 3px 0',
        color: '#666',
        width: '80px',
    },
    addInfoValue: {
        padding: '3px 0',
    },
    totalsTable: {
        width: '100%',
        borderCollapse: 'collapse',
    },
    totalsLabel: {
        padding: '5px 10px',
        textAlign: 'right',
        fontSize: '11px',
    },
    totalsValue: {
        padding: '5px 10px',
        textAlign: 'right',
        fontSize: '12px',
        fontWeight: '500',
    },
    grandTotalRow: {
        backgroundColor: '#333',
        color: '#fff',
    },
    grandTotalLabel: {
        padding: '8px 10px',
        textAlign: 'right',
        fontSize: '14px',
        fontWeight: 'bold',
    },
    grandTotalValue: {
        padding: '8px 10px',
        textAlign: 'right',
        fontSize: '16px',
        fontWeight: 'bold',
    },
    paymentSection: {
        marginBottom: '15px',
        border: '1px solid #dee2e6',
        padding: '10px',
    },
    balanceDueWarning: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px',
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        fontWeight: 'bold',
    },
    balanceLabel: {
        fontSize: '12px',
    },
    balanceValue: {
        fontSize: '14px',
    },
    signatureSection: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '40px',
        paddingTop: '20px',
    },
    signatureBox: {
        width: '150px',
        textAlign: 'center',
    },
    signatureLine: {
        borderTop: '1px solid #333',
        marginBottom: '5px',
    },
    signatureLabel: {
        fontSize: '10px',
        color: '#666',
    },
    footer: {
        marginTop: '30px',
        paddingTop: '15px',
        borderTop: '1px dashed #ccc',
        textAlign: 'center',
    },
    footerText: {
        margin: '0 0 5px 0',
        fontSize: '12px',
        fontWeight: 'bold',
    },
    footerNote: {
        margin: 0,
        fontSize: '10px',
        color: '#666',
        fontStyle: 'italic',
    },
    footerBranding: {
        margin: '8px 0 0 0',
        fontSize: '9px',
        color: '#999',
    },
    imagesSection: {
        marginBottom: '15px',
        border: '1px solid #dee2e6',
        padding: '10px',
    },
    imagesSectionHeader: {
        fontSize: '12px',
        fontWeight: 'bold',
        marginBottom: '8px',
        color: '#333',
    },
    imagesGrid: {
        display: 'flex',
        gap: '15px',
        justifyContent: 'center',
    },
    imageBox: {
        textAlign: 'center',
        flex: '0 0 auto',
    },
    imageImg: {
        maxWidth: '250px',
        maxHeight: '180px',
        objectFit: 'contain',
        border: '1px solid #dee2e6',
    },
    imageLabel: {
        fontSize: '10px',
        color: '#666',
        marginTop: '4px',
    },
};
